import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AtlAppCardProps, AtlCardEvents, CardTags } from './card.types.ts';
import { getGlobalStyleSheets } from '../../global-styles';
import { AtlantisEvent, IClassMap } from '../helpers.ts';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { IconSizes, IconSource } from '../icon/icon.props.ts';
import { enumAttributeConverter } from '../enum-types.ts';

@customElement(CardTags.AppCard)
export class AtlAppCard extends LitElement implements AtlAppCardProps {

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         :host {
            display: block;
         }
      `,
   ];

   @property({ type: String })
   internalId?: string;

   @property({ type: String })
   internalClassName?: string;

   @property({ type: String })
   cardTitle: string;

   @property({ type: Boolean })
   fixedSize?: boolean;

   @property({ type: Boolean })
   cursorPointer?: boolean;

   @property({ type: Boolean })
   hoverElevation?: boolean;

   @property({ type: String })
   bodyTextTitle?: string;

   @property({ type: Number })
   cardTabIndex?: number;

   @property({
      type: String,
      converter: enumAttributeConverter(IconSource, IconSource.FontAwesome),
   })
   iconSource?: IconSource;

   @property({ type: String })
   iconName?: string;

   private _renderIcon() {
      return this.iconName
         ? html` <div class="app-icon">
              <atlantis-icon
                 iconSource="${ifDefined(this.iconSource)}"
                 iconSize="${IconSizes.Small}"
                 name="${this.iconName}"
              >
              </atlantis-icon>
           </div>`
         : nothing;
   }

   private _handleSlotChange = (e: AtlantisEvent<Event, HTMLSlotElement>) => {
      const assignedNodes = e.target.assignedNodes({
         flatten: true,
      }) as HTMLElement[];
      const slotBodyButton = assignedNodes.filter(
         (node) => node.nodeName === 'ATLANTIS-BUTTON'
      ) as HTMLElement[];

      if (slotBodyButton.length > 0) {
         const wrapper = document.createElement('div');
         wrapper.classList.add('d-flex', 'justify-content-start');
         slotBodyButton.forEach((button) => {
            wrapper.appendChild(button);

            const parent = button.parentNode;
            if (parent) {
               parent.replaceChild(wrapper, button);
            }
         });
      }

      return this.updateComplete;
   };

   render() {
      const classNames: IClassMap = {
         card: true,
         'app-card': true,
         'fixed-size': this.fixedSize === true,
         clickable: this.cursorPointer === true,
         'hover-elevation': this.hoverElevation === true,
      };
      if (this.internalClassName) {
         classNames[this.internalClassName] = true;
      }

      return html`
         <div
            id=${ifDefined(this.internalId)}
            class=${classMap(classNames)}
            @click=${() => this._handleCardClick()}
            tabindex=${this.cardTabIndex || 0}
            part="app-card"
         >
            <div class="app-card-header">
               <div class="card-title" title="${ifDefined(this.cardTitle)}">
                  ${this.cardTitle}
               </div>
               ${this._renderIcon()}
               <slot name="slotHeader"></slot>
            </div>
            <div class="card-body">
               <div class="card-text" title=${ifDefined(this.bodyTextTitle)}>
                  <slot
                     name="slotBody"
                     @slotchange=${this._handleSlotChange}
                  ></slot>
               </div>
            </div>
            <slot name="slotFooter"></slot>
         </div>
      `;
   }

   protected _handleCardClick() {
      if (this.cursorPointer) {
         const event = new CustomEvent(AtlCardEvents.AppCardClick, {
            bubbles: true,
            composed: true,
         });
         this.dispatchEvent(event);
      }
   }
}
